import { useState } from 'react';
import { usePageParam } from '../../hooks/usePageParam';
import { useMutation, useQuery } from '@apollo/client';
import Pagination from '../../components/common/Pagination';
import { PROJECT_MEMBERS_QUERY, PROJECT_MEMBER_CANDIDATES_QUERY } from '../../graphql/queries';
import { ADD_PROJECT_MEMBER_MUTATION, REMOVE_PROJECT_MEMBER_MUTATION, UPDATE_MEMBER_ROLE_MUTATION } from '../../graphql/mutations';
import { MEMBER_ROLES, MemberRole, memberRoleLabel } from '../../utils/projectRole';
import styles from './ProjectMembersPanel.module.css';
import Icon from '../../components/common/Icon';

interface Member {
  user: { id: string; firstName: string; lastName: string; email: string; parentUser: { id: string } | null };
  role: string;
  joinedAt: string;
}

interface Candidate { id: string; firstName: string; lastName: string; email: string }

const CANDIDATE_PAGE = 5;

// Who may open the project and what they may do there. The owner (and staff
// with All Projects) add the owner's own subusers and change or remove anyone —
// a previous owner who stayed on after a transfer included.
const ProjectMembersPanel = ({ projectId, ownerId, canManage }: { projectId: string; ownerId: string; canManage: boolean }) => {
  const [search, setSearch] = useState('');
  const [page, setPage] = usePageParam('membersPage');
  const [newRole, setNewRole] = useState<MemberRole>('viewer');
  const [error, setError] = useState('');
  const refetchQueries = ['ProjectMembers', 'ProjectMemberCandidates', 'Project'];
  const onError = (err: Error) => setError(err.message);

  const { data, loading } = useQuery(PROJECT_MEMBERS_QUERY, { variables: { projectId }, fetchPolicy: 'cache-and-network' });
  const { data: candData } = useQuery(PROJECT_MEMBER_CANDIDATES_QUERY, {
    variables: { projectId, page, limit: CANDIDATE_PAGE, search: search || undefined },
    skip: !canManage,
    fetchPolicy: 'cache-and-network',
  });
  const [add, { loading: adding }] = useMutation(ADD_PROJECT_MEMBER_MUTATION, { refetchQueries, onError });
  const [setRole] = useMutation(UPDATE_MEMBER_ROLE_MUTATION, { refetchQueries, onError });
  const [remove] = useMutation(REMOVE_PROJECT_MEMBER_MUTATION, { refetchQueries, onError });

  const members: Member[] = data?.teamMembers?.members ?? [];
  const candidates: Candidate[] = candData?.projectMemberCandidates?.users ?? [];
  const candidateTotal: number = candData?.projectMemberCandidates?.total ?? 0;

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>Members</h3>
      <p className={styles.hint}>
        The owner can do everything. Editors upload and change files; commenters add notes and measurements; viewers only look and download.
      </p>
      {error && <p className={styles.error} role="alert">{error}</p>}

      {loading && members.length === 0 ? (
        <p className={styles.hint}>Loading members…</p>
      ) : members.length === 0 ? (
        <p className={styles.hint}>Nobody else is on this project yet.</p>
      ) : (
        <ul className={styles.list} aria-label="Project members">
          {members.map((m) => (
            <li key={m.user.id} className={styles.row}>
              <div className={styles.person}>
                <span className={styles.name}>{m.user.firstName} {m.user.lastName}</span>
                <span className={styles.email}>{m.user.email}</span>
              </div>
              {m.user.parentUser?.id !== ownerId && <span className={styles.outside} title="Not one of the owner's subusers — for example the previous owner after a transfer">Other account</span>}
              {canManage ? (
                <>
                  <select
                    aria-label={`Role of ${m.user.firstName} ${m.user.lastName}`}
                    value={MEMBER_ROLES.some((r) => r.value === m.role) ? m.role : 'editor'}
                    onChange={(e) => { setError(''); void setRole({ variables: { projectId, userId: m.user.id, role: e.target.value } }); }}
                  >
                    {MEMBER_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  <button
                    type="button"
                    className={styles.remove}
                    aria-label={`Remove ${m.user.firstName} ${m.user.lastName}`}
                    onClick={() => {
                      if (window.confirm(`Take ${m.user.firstName} ${m.user.lastName} off this project?`)) {
                        setError('');
                        void remove({ variables: { projectId, userId: m.user.id } });
                      }
                    }}
                  >
                    <Icon name="person_remove" className="text-base" aria-hidden="true" />
                  </button>
                </>
              ) : (
                <span className={styles.role}>{memberRoleLabel(m.role)}</span>
              )}
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <div className={styles.addBox}>
          <div className={styles.addHead}>
            <span className={styles.addTitle}>Add someone from your team</span>
            <label className={styles.addRole}>
              <span>as</span>
              <select aria-label="Role for new members" value={newRole} onChange={(e) => setNewRole(e.target.value as MemberRole)}>
                {MEMBER_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </label>
          </div>
          <input
            className={styles.search}
            type="search"
            aria-label="Search your team"
            placeholder="Search your team…"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
          {candidates.length === 0 ? (
            <p className={styles.hint}>
              {search ? 'Nobody matches.' : 'Everyone in your team is already here. Invite more people in Team Management.'}
            </p>
          ) : (
            <ul className={styles.list} aria-label="Team members to add">
              {candidates.map((c) => (
                <li key={c.id} className={styles.row}>
                  <div className={styles.person}>
                    <span className={styles.name}>{c.firstName} {c.lastName}</span>
                    <span className={styles.email}>{c.email}</span>
                  </div>
                  <button
                    type="button"
                    className={styles.add}
                    disabled={adding}
                    onClick={() => { setError(''); void add({ variables: { projectId, userId: c.id, role: newRole } }); }}
                  >
                    Add {c.firstName}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Pagination page={page} pageSize={CANDIDATE_PAGE} total={candidateTotal} onPageChange={setPage} noun="people" variant="inline" />
        </div>
      )}
    </div>
  );
};

export default ProjectMembersPanel;
